import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function EmployeeAttendance() {
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const employeeId = user.employeeId || user._id;

  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAtt, setSelectedAtt] = useState(null);

  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

  const formatWorkingHours = (hoursDecimal) => {
    if (!hoursDecimal) return "00:00:00";
    const totalSeconds = Math.round(hoursDecimal * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const formatBreakDuration = (minutes) => {
    if (!minutes) return "00:00:00";
    const totalSeconds = Math.round(minutes * 60);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const getDateKey = (d) => {
    if (!d) return null;
    const dateObj = new Date(d);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTime12H = (timeStr) => {
    if (!timeStr) return "--";
    const parts = String(timeStr).split(":");
    if (parts.length < 2) return timeStr;
    let hour = parseInt(parts[0], 10);
    if (isNaN(hour)) return timeStr;
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${parts[1]} ${ampm}`;
  };

  const fetchAttendance = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/monthly`, {
        params: {
          employeeId,
          month: selectedMonth,
          year: selectedYear,
        },
      });

      setAttendanceData(res.data.data || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      console.error("Attendance Fetch Error:", error);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth, selectedYear]);

  const getIcon = (status) => {
    if (!status) return "-";
    const s = status.toLowerCase();
    if (s === "present") return "P";
    if (s === "absent") return "A";
    if (s === "holiday") return "H";
    if (s === "paid leave" || s === "unpaid leave" || s === "leave") return "L";
    if (s.includes("half")) return "HD";
    return "-";
  };

  const openModal = (att) => {
    if (!att || !att.status || att.status === "-") return;
    setSelectedAtt(att);
    setModalOpen(true);
  };

  const exportToExcel = () => {
    const header = ["Employee"];
    for (let d = 1; d <= daysInMonth; d++) header.push(String(d));

    const row = [user.ename];

    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const att = attendanceData.find((a) => getDateKey(a.date) === dateKey);
      row.push(att ? att.status : "");
    }

    const ws1 = XLSX.utils.aoa_to_sheet([header, row]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, "Summary");
    XLSX.writeFile(wb, `${user.ename}_Attendance_${months[selectedMonth - 1]}_${selectedYear}.xlsx`);
  };

  return (
    <>
      {modalOpen && (
        <div
          style={{
            backdropFilter: "blur(4px)",
            background: "rgba(0,0,0,0.15)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 999,
          }}
          onClick={() => setModalOpen(false)}
        />
      )}

      <h2 className="emp-page-title">Attendance Dashboard</h2>
      <p className="emp-page-subtitle">Track daily presence, late marks, and holidays for the month.</p>

      <section className="emp-panel mb-3">
        <div className="emp-panel-body">
          <div className="emp-toolbar">
            <div className="d-flex gap-2 flex-wrap">
              <select className="form-select w-auto" value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                {months.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>

              <select className="form-select w-auto" value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button className="btn btn-success" onClick={exportToExcel}>
              Export Excel
            </button>
          </div>

          <div className="emp-stat-grid">
            <article className="emp-stat-card">
              <p>Present</p>
              <h3>{summary.present || 0}</h3>
            </article>
            <article className="emp-stat-card">
              <p>Absent</p>
              <h3>{summary.absent || 0}</h3>
            </article>
            <article className="emp-stat-card">
              <p>Leave / Holiday</p>
              <h3>
                {(summary.holiday || 0) +
                  (summary.paidLeaves || 0) +
                  (summary.unpaidLeaves || 0) +
                  (summary.halfday || 0)}
              </h3>
            </article>
          </div>
        </div>
      </section>

      <section className="emp-panel">
        <div className="emp-panel-header">
          <h5 className="mb-1">Monthly Calendar Grid</h5>
          <p className="mb-0 text-muted small">Legend: P Present, A Absent, L Leave, H Holiday, HD Half Day</p>
        </div>

        <div className="emp-panel-body">
          <div className="d-flex fw-semibold mb-2 align-items-center">
            <div style={{ width: "140px", minWidth: "140px" }}>Employee</div>
            <div className="d-flex flex-wrap">
              {Array.from({ length: daysInMonth }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: "34px",
                    minWidth: "34px",
                    textAlign: "center",
                    color: "#475569",
                    fontSize: "12px",
                  }}
                >
                  {i + 1}
                </div>
              ))}
            </div>
          </div>

          <div className="d-flex align-items-center">
            <div style={{ width: "140px", minWidth: "140px", fontWeight: 600 }}>{user.ename || "Employee"}</div>
            <div className="d-flex flex-wrap">
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = String(i + 1).padStart(2, "0");
                const mm = String(selectedMonth).padStart(2, "0");
                const dateKey = `${selectedYear}-${mm}-${day}`;
                const att = attendanceData.find((a) => getDateKey(a.date) === dateKey);

                const hasValidStatus = !!att && !!att.status && att.status !== "-";

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => hasValidStatus && openModal(att)}
                    style={{
                      width: "34px",
                      minWidth: "34px",
                      height: "34px",
                      margin: "0 1px 2px 0",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      background: att ? "#fff" : "#f8fafc",
                      fontSize: "11px",
                      position: "relative",
                      cursor: hasValidStatus ? "pointer" : "default",
                    }}
                  >
                    {att ? getIcon(att.status) : "-"}
                    {att?.isLateMinutes > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: "2px",
                          right: "2px",
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          background: "#ef4444",
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {modalOpen && selectedAtt && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "340px",
            height: "100%",
            background: "white",
            padding: "20px",
            zIndex: 1000,
            boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <h4>Attendance Details</h4>
          <hr />
          <p><b>Date:</b> {getDateKey(selectedAtt.date)}</p>
          <p><b>Status:</b> {selectedAtt.status}</p>
          <p><b>Check In:</b> {formatTime12H(selectedAtt.check_in)}</p>
          <p><b>Check Out:</b> {formatTime12H(selectedAtt.check_out)}</p>
          <p><b>Late By:</b> {selectedAtt.isLateMinutes || 0} minutes</p>
          <p><b>Working Hours:</b> {formatWorkingHours(selectedAtt.workingHours)}</p>
          <p><b>Total Break Duration:</b> {formatBreakDuration(selectedAtt.totalBreakDuration)}</p>

          {selectedAtt.breaks && selectedAtt.breaks.length > 0 && (
            <div className="mt-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
              <h6>Break Sessions:</h6>
              <ul style={{ paddingLeft: "15px", fontSize: "13px" }}>
                {selectedAtt.breaks.map((brk, idx) => (
                  <li key={idx} style={{ marginBottom: "4px" }}>
                    <strong>{idx + 1}:</strong> {brk.start} - {brk.end} ({brk.duration} min)
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button className="btn btn-danger mt-3" onClick={() => setModalOpen(false)}>
            Close
          </button>
        </div>
      )}
    </>
  );
}

export default EmployeeAttendance;
